/**
 * @fileoverview Defines the `ClaudeApi` class for interacting with the Anthropic API, providing `chatCompletion` and `streamChatCompletions` functionalities. It also defines model configurations for `ClaudeSonnetModel`, `ClaudeHaikuModel`, and `ClaudeOpusModel`. The file utilizes types like `Message`, `InputTool`, and `Usage` for request and response handling.
 */

import Anthropic from '@anthropic-ai/sdk'
import { TextDelta } from '@anthropic-ai/sdk/resources'
import { InputTool, LlmApi, Message, ModelNeedingKeys, ResponseToolCall, Usage } from './llm_types'
import { ApiKeys, KeyProvider } from './api_keys'
import { generateObject } from 'ai'
import { anthropic } from '@ai-sdk/anthropic'
import { z } from 'zod'

export const ClaudeOpusModel: ModelNeedingKeys = {
  name: 'claude-4-opus',
  promptTokenCost: 15 / 1000000,
  completionTokenCost: 75 / 1000000,
  getApi: (settings: KeyProvider) => new ClaudeApi('claude-4-opus-20250514', settings),
}

export const ClaudeSonnetModel: ModelNeedingKeys = {
  name: 'claude-4-sonnet',
  promptTokenCost: 3 / 1000000,
  completionTokenCost: 15 / 1000000,
  getApi: (settings: KeyProvider) => new ClaudeApi('claude-4-sonnet-20250514', settings),
}

export const ClaudeHaikuModel: ModelNeedingKeys = {
  name: 'claude-4-5-haiku',
  promptTokenCost: 0.8 / 1000000,
  completionTokenCost: 4 / 1000000,
  getApi: (settings: KeyProvider) => new ClaudeApi('claude-4-5-haiku-latest', settings),
}

export class ClaudeApi implements LlmApi {
  requiredKeys: ApiKeys[] = ['ANTHROPIC_API_KEY']
  private client: Anthropic

  constructor(
    private model: string,
    settings: KeyProvider,
  ) {
    this.client = new Anthropic({
      apiKey: settings.get('ANTHROPIC_API_KEY'),
    })
  }

  private convertToAnthropicMessages(
    system: string | undefined,
    texts: string[],
    conversation: Message[],
  ): Array<Anthropic.MessageParam> {
    const messages: Array<Anthropic.MessageParam> = []

    // Add system message if provided
    if (system) {
      messages.push({
        role: 'assistant',
        content: system,
      })
    }

    // Add any additional text content
    for (const text of texts) {
      messages.push({
        role: 'user',
        content: text,
      })
    }

    // Add conversation history
    for (const msg of conversation) {
      if (msg.role === 'system') {
        continue
      }
      messages.push({
        role: msg.role,
        content: msg.content,
      })
    }

    return messages
  }

  async chatCompletion(
    system: string,
    texts: string[],
    tools: InputTool[],
    conversation: Message[],
  ): Promise<{ message: Message; toolCalls: Array<ResponseToolCall>; usage: Usage }> {
    const messages = this.convertToAnthropicMessages(system, texts, conversation)

    const response = await this.client.messages.create({
      model: this.model,
      messages,
      tools: tools.map((tool) => ({
        input_schema: {
          type: 'object',
          properties: tool.parameters.properties,
          required: tool.parameters.required,
        },
        name: tool.name,
        description: tool.description,
      })),
      max_tokens: 4096,
    })

    const toolCalls: ResponseToolCall[] = []
    for (const responseMessage of response.content) {
      if (responseMessage.type === 'tool_use') {
        toolCalls.push({
          name: responseMessage.name,
          args: JSON.stringify(responseMessage.input),
        })
      }
    }
    const message: Message = {
      role: 'assistant',
      content: '',
    }
    if (response.content[0] && response.content[0].type === 'text') {
      message.content = response.content[0].text
    }

    return {
      message,
      toolCalls,
      usage: {
        promptTokens: response.usage.input_tokens,
        completionTokens: response.usage.output_tokens,
      },
    }
  }

  async streamChatCompletions(
    system: string | undefined,
    texts: string[],
    conversation: Message[],
    handler: (message: any) => void,
  ): Promise<{ message: Message; usage: Usage; inputMessages: Message[] }> {
    const messages = this.convertToAnthropicMessages(system, texts, conversation)

    const stream = await this.client.messages.create({
      model: this.model,
      messages,
      stream: true,
      max_tokens: 4096,
    })

    let fullContent = ''
    let usage: Usage = {
      promptTokens: 0,
      completionTokens: 0,
    }

    for await (const chunk of stream) {
      if (chunk.type === 'message_start') {
        usage.promptTokens = chunk.message.usage?.input_tokens || 0
      } else if (chunk.type === 'content_block_start') {
        // Handle content block start if needed
      } else if (chunk.type === 'content_block_delta') {
        fullContent += (chunk.delta as TextDelta).text || ''
        handler((chunk.delta as TextDelta).text || '')
      } else if (chunk.type === 'message_delta') {
        usage.completionTokens = chunk.usage?.output_tokens || 0
      }
    }

    return {
      message: {
        role: 'assistant',
        content: fullContent,
      },
      usage,
      inputMessages: messages as Message[],
    }
  }
}

export async function promptClaudeWithStructuredOutput<T>(
  model: 'claude-3-5-sonnet-latest' | 'claude-3-5-haiku-latest',
  system: string,
  prompt: string,
  schema: z.ZodSchema<T>,
): Promise<{ object: T; usage: Usage }> {
  const { object, usage } = await generateObject({
    model: anthropic(model),
    system,
    prompt,
    schema,
  })
  return { object, usage }
}
